import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { UserMetricsType } from "@lifeat/shared/data-models/user/UserMetrics/UserMetricsEntity";
import { DEFAULT_TIME_SPENT_OBJ } from "@lifeat/shared/user/TimeSpentUserMetricsConstants";
import * as DateUtil from "@lifeat/shared/util/DateUtil";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import LifeAtModal from "@common/LifeAtModal/LifeAtModal";
import LoadingSpinner from "@common/LoadingSpinner/LoadingSpinner";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { UserMetricsInteractEvent } from "@user/AmplitudeUserEvents";
import * as AllUserMetricsService from "../../service/AllUserMetricsService";
import UserMetricDetailedView from "./UserMetricDetailedView";
import UserMetricSummaryView from "./UserMetricSummaryView";

const NUM_DAYS_TO_FETCH = 14; // total days worth of data to be fetched
const DEFAULT_LONGEST_STREAK = 1;
const MS_TO_HRS = 3600000;
const LONGEST_STREAK_TITLE = "Longest streak";
const TIME_SPENT_TITLE = "Hours on app";
const FOCUS_TIME_TITLE = "Pomodoro hours";
const MODAL_TITLE = "Your stats";

const VerticalDivider = styled.div`
    width: 90%;
    height: 1px;
    border-top: 1px solid ${(props) => props.theme.borderPrimary};
    align-self: center;
    margin: 20px;
`;

const Footer = styled.div`
    display: flex;
    width: 100%;
    justify-content: flex-start;
`;

const ModalElementsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

export interface UserMetricsModalProps {
    readonly isOpen: boolean;
    readonly closeModal: () => void;
}

export default function UserMetricsModal(props: UserMetricsModalProps) {
    const amplitudeService = AmplitudeService.getInstance();

    const { user, userData }: UserContextType = useUserContext();

    const [userMetrics, setUserMetrics] = useState<UserMetricsType>(DEFAULT_TIME_SPENT_OBJ);
    const [longestStreakCount, setLongestStreakCount] = useState<number>(DEFAULT_LONGEST_STREAK);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        setLongestStreakCount(userData?.metrics?.streakMetrics?.maxDailyStreak || DEFAULT_LONGEST_STREAK);
    }, [user, userData]);

    useEffect(() => {
        if (user && userData && props.isOpen) {
            // Log event in amplitude for opening metrics modal
            amplitudeService.logEvent(new UserMetricsInteractEvent());

            fetchTimeSpent(userData?.proUserInfo?.isProUser);
        }
    }, [props.isOpen]);

    const fetchTimeSpent = useCallback((fetchFocusTime: boolean) => {
        setIsLoading(true);

        const today = new Date();
        const endDate = DateUtil.formatDateStringYYYYMMDD(today);

        today.setDate(today.getDate() - NUM_DAYS_TO_FETCH);
        const startDate = DateUtil.formatDateStringYYYYMMDD(today);

        const updateTimeSpentObj = (fetchedTimes: UserMetricsType) => {
            const timeSpentTemp: UserMetricsType = userMetrics;
            timeSpentTemp.dailyTimeSpent = { ...userMetrics.dailyTimeSpent, ...fetchedTimes.dailyTimeSpent };
            timeSpentTemp.totalTimeSpent = fetchedTimes.totalTimeSpent;

            if (fetchFocusTime) {
                timeSpentTemp.focusTime = fetchedTimes.focusTime;
            }

            setUserMetrics({ ...timeSpentTemp });

            setIsLoading(false);
        };
        AllUserMetricsService.fetchUserMetrics({ startDate: startDate, endDate: endDate }, updateTimeSpentObj);
    }, []);

    const getSummaryViewInfo = (): { title: string; content: string }[] => {
        const summaryViewInfo: { title: string; content: string }[] = [
            {
                title: LONGEST_STREAK_TITLE,
                content: `🔥 ${longestStreakCount}`,
            },
            {
                title: TIME_SPENT_TITLE,
                content: `⏳ ${Math.floor(userMetrics.totalTimeSpent / MS_TO_HRS)}`,
            },
        ];

        if (userData?.proUserInfo?.isProUser) {
            summaryViewInfo.push({
                title: FOCUS_TIME_TITLE,
                content: `⏱ ${Math.floor(userMetrics.focusTime / MS_TO_HRS)}`,
            });
        }

        return summaryViewInfo;
    };

    const modalContentMetrics = (
        <>
            <UserMetricSummaryView metrics={getSummaryViewInfo()} />
            <VerticalDivider className={`vertical-divider`} />
            <UserMetricDetailedView
                data={userMetrics.dailyTimeSpent}
                maxDays={NUM_DAYS_TO_FETCH}
                onClose={props.closeModal}
            />
        </>
    );

    const modalContentElements = (
        <ModalElementsWrapper className={`user-metrics-modal-elements-wrapper`}>
            {isLoading ? <LoadingSpinner size={20} /> : modalContentMetrics}
        </ModalElementsWrapper>
    );

    return (
        <LifeAtModal
            key={`user-metric-modal-lifeat-modal`}
            title={MODAL_TITLE}
            isOpen={props.isOpen}
            closeModal={props.closeModal}
            className={`user-metrics-modal`}
        >
            {modalContentElements}
        </LifeAtModal>
    );
}
